"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

import { selectThreadListByUserIdAction } from "@/app/api/chat/actions";
import { appStore } from "@/app/store";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserRole } from "@/hooks/use-user-role";
import { authClient } from "auth/client";
import { Session, User } from "better-auth";
import { Shortcuts, isShortcutEvent } from "lib/keyboard-shortcuts";
import {
  ChevronRight,
  Command,
  Languages,
  LogOut,
  MessageCircle,
  MessageSquarePlus,
  Palette,
  PanelLeft,
  Settings,
  Waypoints,
} from "lucide-react";
import { ChevronsUpDown } from "lucide-react";
import useSWR from "swr";
import { Avatar, AvatarFallback, AvatarImage } from "ui/avatar";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { useShallow } from "zustand/shallow";
import { useCustomSidebar } from "./custom-sidebar-layout";
import { AgentsSidebarSection } from "../agents/agents-sidebar-section";

function SimpleMenus() {
  const router = useRouter();
  const { isAdmin } = useUserRole();

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        className="w-full justify-start font-medium text-sm h-8 px-2"
        data-tour="new-chat-button"
        onClick={() => {
          router.push("/");
          router.refresh();
        }}
      >
        <MessageSquarePlus className="size-4 mr-2" />
        New Chat
      </Button>

      {isAdmin() && (
        <Button
          variant="ghost"
          className="w-full justify-start font-medium text-sm h-8 px-2"
          data-tour="mcp-config"
          onClick={() => router.push("/mcp")}
        >
          <Settings className="size-4 mr-2" />
          MCP Configuration
        </Button>
      )}

      <Button
        variant="ghost"
        className={`w-full justify-start font-medium text-sm h-8 px-2 ${!isAdmin() ? "opacity-60 cursor-not-allowed" : ""
          }`}
        data-tour="workflows"
        onClick={() => {
          if (isAdmin()) {
            router.push("/workflow");
          }
        }}
        disabled={!isAdmin()}
      >
        <Waypoints className="size-4 mr-2" />
        <span className="flex-1 text-left">Workflow</span>
        {!isAdmin() && (
          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 ml-2">
            Coming Soon
          </Badge>
        )}
      </Button>
    </div>
  );
}

function ProjectsSection() {
  return (
    <div className="space-y-2" data-tour="projects-section">
      <h3 className="text-sm font-medium text-muted-foreground px-2">
        Projects
      </h3>
      <Button variant="ghost" className="w-full justify-start text-sm h-8 px-2">
        <span className="mr-2">📁</span>
        Create a project
      </Button>
      <div className="text-xs text-muted-foreground px-2">
        To organize your ideas
      </div>
    </div>
  );
}

function RecentChatsSection() {
  const router = useRouter();
  const [threadList, currentThreadId, storeMutate] = appStore(
    useShallow((state) => [
      state.threadList,
      state.currentThreadId,
      state.mutate,
    ]),
  );

  // Fetch threads using SWR like the original sidebar
  const { data: fetchedThreads, isLoading } = useSWR(
    "threads",
    selectThreadListByUserIdAction,
    {
      fallbackData: [],
      onSuccess: (data) => {
        console.log("Fetched threads:", data);
        storeMutate({ threadList: data });
      },
      onError: (error) => {
        console.error("Failed to fetch threads:", error);
      },
    },
  );

  // Use fetched threads or fallback to store with better error handling
  const displayThreads = Array.isArray(fetchedThreads)
    ? fetchedThreads
    : Array.isArray(threadList)
      ? threadList
      : [];
  console.log("Display threads:", displayThreads);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground px-2">
          Recent Chats
        </h3>
        <div className="text-sm text-muted-foreground px-2">Loading...</div>
      </div>
    );
  }

  if (!Array.isArray(displayThreads) || displayThreads.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground px-2">
          Recent Chats
        </h3>
        <div className="text-sm text-muted-foreground px-2">
          No conversations yet
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-tour="recent-chats">
      <h3 className="text-sm font-medium text-muted-foreground px-2">
        Recent Chats
      </h3>
      <div className="space-y-1">
        {displayThreads.slice(0, 10).map((thread) => (
          <Button
            key={thread.id}
            variant="ghost"
            className={`w-full justify-start text-sm h-8 px-2 ${currentThreadId === thread.id ? "bg-accent" : ""
              }`}
            onClick={() => router.push(`/chat/${thread.id}`)}
          >
            <span className="truncate">{thread.title}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

function CustomUserDropdown({
  session,
}: { session: { session: any; user: any } }) {
  const appStoreMutate = appStore((state) => state.mutate);
  const user = session?.user;
  const router = useRouter();
  const { isAdmin } = useUserRole();

  const logout = () => {
    authClient.signOut().finally(() => {
      window.location.href = "/sign-in";
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-start h-12 px-2 bg-input/30 border"
          data-tour="user-menu"
        >
          <Avatar className="h-8 w-8 rounded-full">
            <AvatarImage
              src={user?.image || "/pf.png"}
              alt={user?.name || ""}
            />
            <AvatarFallback>
              {user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 text-left ml-2">
            <div className="text-sm font-medium truncate">
              {user?.name || user?.email}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.email}
            </div>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" className="w-60" align="center">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <Avatar className="h-8 w-8 rounded-full">
              <AvatarImage
                src={user?.image || "/pf.png"}
                alt={user?.name || ""}
              />
              <AvatarFallback>
                {user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">
                  {user?.name || "Thomas Burns"}
                </span>
                {isAdmin() && (
                  <Badge
                    variant="destructive"
                    className="text-xs px-1.5 py-0.5"
                  >
                    admin
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer"
          data-tour="chat-preferences"
          onClick={() => appStoreMutate({ openChatPreferences: true })}
        >
          <Settings className="mr-2 h-4 w-4" />
          Chat Preferences
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          data-tour="settings"
          onClick={() => router.push("/settings")}
        >
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>

        {isAdmin() && (
          <DropdownMenuItem
            className="cursor-pointer"
            data-tour="admin-panel"
            onClick={() => router.push("/admin")}
          >
            <Settings className="mr-2 h-4 w-4" />
            Admin Panel
          </DropdownMenuItem>
        )}

        <DropdownMenuItem className="cursor-pointer" data-tour="theme-selector">
          <Palette className="mr-2 h-4 w-4" />
          <span className="mr-auto">Theme</span>
          <span className="text-muted-foreground text-xs ml-auto mr-2">
            Light Default
          </span>
          <ChevronRight className="h-4 w-4" />
        </DropdownMenuItem>

        <DropdownMenuItem className="cursor-pointer" data-tour="language-selector">
          <Languages className="mr-2 h-4 w-4" />
          <span className="mr-auto">Language</span>
          <ChevronRight className="h-4 w-4" />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer"
          data-tour="keyboard-shortcuts"
          onClick={() => appStoreMutate({ openShortcutsPopup: true })}
        >
          <Command className="mr-2 h-4 w-4" />
          Keyboard Shortcuts
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer"
          data-tour="roadmap-feedback"
          onClick={() =>
            window.open("https://roadmap.successstrategy.design/", "_blank")
          }
        >
          <svg className="mr-2 h-4 w-4 fill-current" viewBox="0 0 24 24">
            <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" />
            <path
              d="M9 12l2 2 4-4"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          Roadmap and Bugs
        </DropdownMenuItem>

        <DropdownMenuItem className="cursor-not-allowed opacity-60">
          <MessageCircle className="mr-2 h-4 w-4" />
          <span className="flex-1">Join Community</span>
          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 ml-2">
            Coming Soon
          </Badge>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={logout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CustomAppSidebar({
  session,
}: { session?: { session: Session; user: User } }) {
  const { toggle } = useCustomSidebar();
  const router = useRouter();
  const isMobile = useIsMobile();

  // global shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isShortcutEvent(e, Shortcuts.openNewChat)) {
        e.preventDefault();
        router.push("/");
        router.refresh();
      }
      if (isShortcutEvent(e, Shortcuts.toggleSidebar)) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [router, toggle]);

  return (
    <div className="flex flex-col h-full border-r bg-background">
      {/* Header */}
      <div className="p-3 border-b">
        <Link
          href="/"
          onClick={(e) => {
            e.preventDefault();
            router.push("/");
            router.refresh();
          }}
          className="flex items-center gap-2 hover:opacity-80"
        >
          <h4 className="font-semibold text-lg">Office Manager GPT</h4>
          {isMobile && (
            <div
              className="ml-auto"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggle();
              }}
            >
              <PanelLeft className="size-4" />
            </div>
          )}
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        <div className="flex flex-col gap-4 overflow-y-auto p-2">
          <SimpleMenus />
          <AgentsSidebarSection />
          <ProjectsSection />
          <RecentChatsSection />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t p-2">
        {session && <CustomUserDropdown session={session} />}
      </div>
    </div>
  );
}
